/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useState, useEffect } from 'react';
import { Alert, Row, Col } from 'reactstrap';
import { useFormikContext } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Field } from '@cordata/buffet';

import useSlice from '~/utils/slice/hook';

const SliceFields = ({ fork }) => {
  const [organizations, setOrganizations] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [subSpecialties, setSubSpecialties] = useState([]);
  const {
    values, setFieldValue, dirty, resetForm,
  } = useFormikContext();
  const { fetch } = useSlice();

  const {
    marketUid, specialtyUid, subSpecialtyUid, orgUid, divisionUid,
  } = values;

  const [shouldWarnUser, setShouldWarnUser] = useState(false);

  const resetContext = () => {
    resetForm({
      ...values,
      market: null,
      specialty: null,
      subSpecialty: null,

      division: null,
      org: null,
    });

    if (marketUid) {
      fetch('market', {
        id: marketUid,
      })
        .then(r => r.data.data)
        .then((r) => {
          resetForm({ market: r });
        });
    }

    if (specialtyUid) {
      fetch('specialty', {
        id: specialtyUid,
      })
        .then(r => r.data.data)
        .then((r) => {
          resetForm({ specialty: r });
        });
    }

    if (subSpecialtyUid) {
      fetch('sub-specialty', {
        id: subSpecialtyUid,
      })
        .then(r => r.data.data)
        .then((r) => {
          resetForm({ subSpecialty: r });
        });
    }

    if (orgUid) {
      fetch('organization', {
        id: orgUid,
      })
        .then(r => r.data.data)
        .then((r) => {
          resetForm({ org: r });
        });
    }

    if (divisionUid) {
      fetch('division', {
        id: divisionUid,
      })
        .then(r => r.data.data)
        .then((r) => {
          resetForm({ division: r });
        });
    }
  };

  useEffect(() => {
    if (values.id && (((values.market ? values.market.id : null) !== marketUid) ||
        ((values.specialty ? values.specialty.id : null) !== specialtyUid) ||
        ((values.subSpecialty ? values.subSpecialty.id : null) !== subSpecialtyUid) ||
        ((values.org ? values.org.id : null) !== orgUid) ||
        ((values.division ? values.division.id : null) !== divisionUid))) {
      setShouldWarnUser(true);
    } else {
      setShouldWarnUser(false);
    }
  }, [
    values.market,
    values.specialty,
    values.subSpecialty,
    values.org,
    values.division,
  ]);

  useEffect(() => {
    fetch('organization')
      .then(r => r.data.data)
      .then(setOrganizations);
    fetch('market')
      .then(r => r.data.data)
      .then(setMarkets);
  }, []);

  useEffect(() => {
    setSpecialties([]);

    if (values.market) {
      fetch('specialty', {
        'market-uid': values.market.id,
      })
        .then(r => r.data.data)
        .then((r) => {
          setSpecialties(r);
        });

      if (dirty) {
        setFieldValue('org', null);
        setFieldValue('division', null);

        setFieldValue('specialty', null);
        setFieldValue('subSpecialty', null);
      }
    } else {
      setFieldValue('specialty', null);
      setFieldValue('subSpecialty', null);
    }
  }, [values.market]);

  useEffect(() => {
    setDivisions([]);

    if (values.org) {
      fetch('division', {
        'org-uid': values.org.id,
      })
        .then(r => r.data.data)
        .then((r) => {
          setDivisions(r);
        });

      if (dirty) {
        setFieldValue('division', null);

        setFieldValue('market', null);
        setFieldValue('specialty', null);
        setFieldValue('subSpecialty', null);
      }
    } else {
      setFieldValue('division', null);
    }
  }, [values.org]);

  useEffect(() => {
    setSubSpecialties([]);

    if (values.specialty) {
      fetch('sub-specialty', {
        'specialty-uid': values.specialty.id,
      })
        .then(r => r.data.data)
        .then((r) => {
          setSubSpecialties(r);
        });

      if (dirty) {
        setFieldValue('org', null);
        setFieldValue('division', null);

        setFieldValue('subSpecialty', null);
      }
    } else {
      setFieldValue('subSpecialty', null);
    }
  }, [values.specialty]);

  useEffect(() => {
    if (values.division) {
      setFieldValue('market', null);
      setFieldValue('specialty', null);
      setFieldValue('subSpecialty', null);
    }
  }, [values.division]);

  useEffect(() => {
    if (values.subSpecialty) {
      setFieldValue('org', null);
      setFieldValue('division', null);
    }
  }, [values.subSpecialty]);

  return (
    <>
      {(!fork || fork.startsWith('m')) &&
        <Row form>
          <Col sm>
            <Field
              name="market"
              type="select"
              options={markets}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.marketName}
            />
          </Col>

          <Col sm>
            <Field
              name="specialty"
              type="select"
              options={specialties}
              noOptionsMessage={() => 'You must select a market first.'}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.specialtyName}
            />
          </Col>

          <Col sm>
            <Field
              name="subSpecialty"
              type="select"
              options={subSpecialties}
              noOptionsMessage={() => 'You must select a specialty first.'}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.subSpecialtyName}
            />
          </Col>
        </Row>
      }

      {(!fork || fork.startsWith('o')) &&
        <Row form>
          <Col sm>
            <Field
              name="org"
              label="Organization"
              type="select"
              options={organizations}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.orgName}
            />
          </Col>

          <Col sm>
            <Field
              name="division"
              type="select"
              options={divisions}
              noOptionsMessage={() => 'You must select an organization first.'}
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.divisionName}
            />
          </Col>
        </Row>
      }

      {shouldWarnUser && (
        <Alert color="danger" id="contextwarning">
          <p>
            <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" fixedWidth />

            <span className="font-weight-bold">WARNING!</span> You are about to change the context of this item. This could result in users losing access to data or other unexpected changes. Please be sure you know what you are doing. If you have any questions, please contact someone familiar with reference data configuration.
          </p>

          <a href="#contextwarning" className="alert-link" onClick={resetContext}>This is not what I want to do. Please reset this item&apos;s context.</a>
        </Alert>
      )}
    </>
  );
};

export default SliceFields;
